import cx from 'clsx';
import * as React from 'react';

import Asset from '@/components/AssetLayer/Asset';
import LottieAnimation from '@/components/Lottie/Lottie';
import {
  TileSize,
  getSizedSources,
  prepareImages,
} from '@/components/modular-grid/ModularGridGameTile/ModularGridGameTile';
import { GameTileProps } from '@/components/modular-grid/types';
import {
  verifyImage,
  verifyLottieAnimation,
} from '@/components/modular-grid/utils';
import { UserSessionContext } from '@/components/UserSessionProvider';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { isEqual } from '@/utils/lodash-alternatives';

import styles from './AnimatedAssetLayer.module.scss';
import {
  DEFAULT_ANIMATION_INTERVAL,
  MIN_ANIMATION_DELAY,
  RANDOM_ANIMATION_INTERVAL,
} from './constants';

type AnimatedAssetLayerProps = {
  tileSize: TileSize;
  isLazy: boolean;
  game: GameTileProps;
};

const getAnimationDelay = (interval?: number) => {
  if (interval)
    return {
      animationDelay: `${interval * 1000}ms`,
    };

  return {
    animationDelay: `${Math.floor(Math.random() * RANDOM_ANIMATION_INTERVAL) +
      MIN_ANIMATION_DELAY}ms`,
  };
};

const AnimatedAssetLayer: React.FC<AnimatedAssetLayerProps> = React.memo(
  ({ game, isLazy, tileSize }) => {
    const { loggedIn } = React.useContext(UserSessionContext);

    const {
      loading: loadingAnimation,
      animation,
      sizedImages,
      sizedImagesLoggedIn,
      image,
      image_logged_in,
      logo: logoImage,
      sizedImagesForLogo,
    } = game;

    const gameSizedImages =
      loggedIn && sizedImagesLoggedIn ? sizedImagesLoggedIn : sizedImages;
    const gameImage = loggedIn && image_logged_in ? image_logged_in : image;
    const { ref, intersected } = useIntersectionObserver(!isLazy);

    if (!animation) return null;
    const fallbackBackgroundColor = 'rgb(0, 20, 60)';
    const { asset: animationAsset } = animation;

    return (
      <div className={cx(styles.wrapper)} ref={ref}>
        {gameImage && (
          <Asset
            assetName={'animationBackground'}
            intersected={intersected}
            loadingAnimation={loadingAnimation}
            fallbackBackgroundColor={fallbackBackgroundColor}
            type={'image'}
            alt={gameImage.alt}
            sizedSources={getSizedSources(gameSizedImages, gameImage, tileSize)}
            imageSrc={gameImage.source}
            isAnimationAsset={true}
          />
        )}
        {logoImage && (
          <Asset
            assetName={'animationLogo'}
            intersected={intersected}
            loadingAnimation={loadingAnimation}
            type={'image'}
            alt={logoImage?.alt}
            sizedSources={getSizedSources(
              sizedImagesForLogo,
              logoImage!,
              tileSize
            )}
            imageSrc={logoImage?.source}
            isAnimationAsset={true}
          />
        )}

        {animationAsset && verifyLottieAnimation(animationAsset) && (
          <LottieAnimation
            delay={(animation.interval || DEFAULT_ANIMATION_INTERVAL) * 1000}
            asset={{
              source: animationAsset.source.base!,
              alt: animationAsset.alt,
              type: 'application',
              subtype: 'json',
            }}
          />
        )}

        {verifyImage(animationAsset) && (
          <Asset
            assetName={'animationEffect'}
            intersected={intersected}
            loadingAnimation={loadingAnimation}
            type={'image'}
            alt={animationAsset!.alt}
            sizedSources={prepareImages(animationAsset!, tileSize)}
            imageSrc={animationAsset!.source}
            isAnimationAsset={true}
            animationStyles={getAnimationDelay(animation.interval)}
          />
        )}
      </div>
    );
  },
  (prevProps: any, nextProps: any) => isEqual(prevProps, nextProps)
);

export default AnimatedAssetLayer;
